import { useCallback, useSyncExternalStore } from "react";
import { budgetSubscription } from "@/lib/firestore/subscriptions/budget";
import { NO_WEDDING_KEY } from "@/lib/firestore/subscription-keys";
import {
  BUDGET_SNAPSHOT_EMPTY,
  BUDGET_SNAPSHOT_LOADING,
} from "@/lib/firestore/subscription-snapshots";
import type { BudgetItem } from "@amazing-wedding/shared-types";

/**
 * Subscribes to /weddings/{wid}/budgetItems ordered by createdAt ASC.
 * Backed by a refcounted shared Firestore subscription keyed by weddingId.
 */
export function useBudget(weddingId: string | null): { items: BudgetItem[]; loading: boolean } {
  const key = weddingId ?? NO_WEDDING_KEY;

  const subscribe = useCallback(
    (listener: () => void) => {
      if (!weddingId) return () => {};
      return budgetSubscription.subscribe(key, listener);
    },
    [weddingId, key],
  );

  const getSnapshot = useCallback(() => {
    if (!weddingId) return BUDGET_SNAPSHOT_EMPTY;
    return budgetSubscription.getSnapshot(key);
  }, [weddingId, key]);

  const snapshot = useSyncExternalStore(subscribe, getSnapshot, () => BUDGET_SNAPSHOT_LOADING);

  if (!weddingId) {
    return { items: BUDGET_SNAPSHOT_EMPTY.data, loading: false };
  }

  return { items: snapshot.data, loading: snapshot.loading };
}
